import {
    Component,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { CmpsDialog } from '@cmps/ng-uikit';
import { tap } from 'rxjs/operators';

import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../../common/take-until-destroy.decorator';
import { Sort } from '../../../../insight/experimental.grid';
import { StateToProps } from '../../../../store';
import { useDispatcher, UseStore } from '../../../../store.decorator';
import { useSelector2 } from '../../../store-use-selector';
import { CommitmentDetailsDialogComponent } from '../commitment-details-dialog/commitment-details-dialog.component';
import { CommitmentDialogData } from '../commitment-details-dialog/model/commitment-detail-dialog-data.model';
import { ViewRow, WpDataRowParam } from '../core';
import {
    Column,
    ExperimentalNgGridComponent,
    GridNode,
    RowNode,
    SortOrder,
} from '../grid';

import { setRowExpanded } from './function/create-row-values.function';
import {
    createRows,
    createTableData,
} from './function/create-table-data.function';
import { TranslationKeyMap } from './main.i18n';
import { mainReducer } from './main.reducer';
import { MainState } from './main.state';
import { ColumnId } from './model/column-id.model';
import { TableData } from './model/table-data.model';
import { expandRow, expandRowCancel } from './state/expand-row.action';
import { reload } from './state/reload.action';
import { sortRows } from './state/sort-rows.action';

@Component({
    selector: 'cmps-coaching-wp-main',
    templateUrl: './main.component.html',
    host: {
        class: 'cmps-coaching-wp-main',
    },
})
@TakeUntilDestroy()
@UseStore<MainState>('coachingInsight.wp.main', mainReducer)
export class MainComponent
    implements
        OnChanges,
        OnInit,
        OnDestroy,
        StateToProps<MainState>,
        TakeUntilDestroy
{
    state: MainState;

    @ViewChild(ExperimentalNgGridComponent)
    private readonly _grid: ExperimentalNgGridComponent;

    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _ColumnId = ColumnId;

    /** @private */
    _SortOrder = SortOrder;

    @Input() userId: number;

    /** @private */
    _tableData: TableData;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _dialog: CmpsDialog,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (this.state == null) {
            return;
        }

        if (changes?.userId != null) {
            useDispatcher(this, reload({ userId: this.userId }));
        }
    }

    ngOnInit(): void {
        useSelector2<ViewRow[]>(
            (state) => (state?.coachingInsight?.wp?.main as MainState)?.rows,
        )
            .pipe(
                tap(([_, state]) => {
                    this._tableData = createTableData(
                        state,
                        this.state.sortOptions,
                        this.state.config,
                        this.state.table,
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useSelector2<any[]>(
            (state) =>
                (state?.coachingInsight?.wp?.main as MainState)?.otherRows,
        )
            .pipe(
                tap(([_, state]) => {
                    try {
                        const parentRow = this._grid?.renderer.rows.find(
                            (e) => e.def.id === state[0].rowId,
                        );
                        const rows = createRows(
                            state[0].rows,
                            this._tableData.rendererOpts.columns,
                            this.state.sortOptions,
                            this.state.config,
                            this.state.table,
                        );
                        void parentRow
                            ?.setChildren(rows)
                            .then(() => this._grid?.onResize());
                    } catch (err) {
                        console.log(err);
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useDispatcher(this, reload({ userId: this.userId }));
    }

    ngOnDestroy(): void {
        //
    }

    /** @private */
    _onDataRangeChange(dataRangeIdEvt: Event): void {
        useDispatcher(
            this,
            reload({
                userId: this.userId,
                dataRangeId: parseInt(
                    (dataRangeIdEvt.target as HTMLOptionElement).value,
                ),
                evalRangeId: this.state.selectedEvalRangeId,
                drillPathId: this.state.selectedDrillPathId,
            }),
        );
    }

    /** @private */
    _onEvalRangeChange(evalRangeIdEvt: Event): void {
        useDispatcher(
            this,
            reload({
                userId: this.userId,
                dataRangeId: this.state.selectedDataRangeId,
                evalRangeId: parseInt(
                    (evalRangeIdEvt.target as HTMLOptionElement).value,
                ),
                drillPathId: this.state.selectedDrillPathId,
            }),
        );
    }

    /** @private */
    _onDrillPathChange(drillPathIdEvt: Event): void {
        useDispatcher(
            this,
            reload({
                userId: this.userId,
                dataRangeId: this.state.selectedDataRangeId,
                evalRangeId: this.state.selectedEvalRangeId,
                drillPathId: (drillPathIdEvt.target as HTMLOptionElement).value,
            }),
        );
    }

    /** @private */
    _onResetGridClick(): void {
        useDispatcher(
            this,
            reload({
                userId: this.userId,
                dataRangeId: this.state.selectedDataRangeId,
                evalRangeId: this.state.selectedEvalRangeId,
                drillPathId: this.state.selectedDrillPathId,
                reset: true,
            }),
        );
    }

    /** @private */
    _onColumnSortClick(c: Column): void {
        this._ngZone.run(() => {
            useDispatcher(this, sortRows({ columnId: c.id }));

            // sortRows() has no side effect, so this is fine
            const determineDepth = (n: RowNode): number => {
                let depth = 0;
                let parent: any = n.parent;
                while (parent != null) {
                    depth++;
                    parent = parent.parent;
                }
                return depth;
            };
            const maxDepthParentDepth =
                this._grid.gridNode.reduce<number>((p, c) => {
                    return Math.max(determineDepth(c), p);
                }, 0) - 1;
            const sortDefs: Sort[] = [this.state.sortOptions];
            if (this.state.sortOptions.columnId !== ColumnId.LEFT) {
                sortDefs.push({
                    columnId: ColumnId.LEFT,
                    order: SortOrder.ASC,
                });
            }
            if (maxDepthParentDepth >= 0) {
                this._grid?.gridNode
                    .filter((n) => {
                        return determineDepth(n) === maxDepthParentDepth;
                    })
                    .forEach((n) => {
                        void n.sort(sortDefs).then(() => {
                            void this._grid?.renderer.layout();
                        });
                    });
            } else {
                void this._grid?.gridNode.sort(sortDefs).then(() => {
                    void this._grid?.renderer.layout();
                });
            }
        });
    }

    /** @private */
    _onRowClick(r: RowNode): void {
        this._ngZone.run(() => {
            // row cache row children array may not have ref to actual row. get it here
            const ar = this?._grid.renderer.rows.find(
                (e) => e.def.id === r.def.id,
            );

            if (ar.context.ctx.values.canExpand !== true) {
                return;
            }

            if (ar.context.ctx.values.isExpanded === true) {
                useDispatcher(this, expandRowCancel({ rowId: ar.def.id }));

                setRowExpanded(ar.def, false);
                ar.detectChanges();
                void ar.removeChildren().then(() => this._grid?.onResize());

                return;
            }

            setRowExpanded(ar.def, true);
            ar.detectChanges();

            const viewRow = ar.context.ctx.data as ViewRow;
            const drillPath = this.state.config.drillPaths.find(
                (e) => e.identifier === this.state.table.drillPathIdentifier,
            );
            const segment = drillPath?.segments?.find(
                (e) => e.identifier === viewRow.segmentIdentifier,
            );
            const nextSegment = drillPath?.segments?.find(
                (e) => e.index > segment?.index,
            );

            const parentRowParams: WpDataRowParam[] = [];
            let pr: RowNode | GridNode = ar;
            do {
                const vr = pr.context?.ctx?.data as ViewRow;
                if (vr == null) {
                    break;
                }
                parentRowParams.push({
                    segmentIdentifier: vr.segmentIdentifier,
                    managerId: vr.manager?.userId,
                    userId: vr.user?.userId,
                    dataRangeId: vr.dataRange?.dataRangeId,
                    endDateDataRange:
                        vr.row.individual?.endDateDataRange ??
                        vr.row.peopleDetail?.endDateDataRange,
                    evaluationRangeId: vr.evaluationRange?.evaluationRangeId,
                    metricId: vr.metric?.metricId,
                    behaviourId: vr.behaviour?.behaviourId,
                });
                pr = pr.parent;
            } while (pr != null);

            useDispatcher(
                this,
                expandRow({
                    rowId: r.def.id,
                    drillPathIdentifier: this.state.table.drillPathIdentifier,
                    segmentIdentifier: nextSegment.identifier,
                    paramsAtSegment: {
                        parentRowParams: parentRowParams.reverse(),
                        rootParam: {
                            userId: viewRow.user?.userId,
                            dataRangeId: viewRow.dataRange?.dataRangeId,
                            endDateDataRange:
                                viewRow.row.individual?.endDateDataRange ??
                                viewRow.row.peopleDetail?.endDateDataRange,
                            evaluationRangeId:
                                viewRow.evaluationRange?.evaluationRangeId,
                        },
                    },
                }),
            );
        });
    }

    /** @private */
    _onViewCommitmentDetailsClick(userId: number): void {
        const commitmentDialogData = new CommitmentDialogData();
        commitmentDialogData.userId = userId;
        commitmentDialogData.dataRangeId = this.state.selectedDataRangeId;
        commitmentDialogData.evaluationRangeId = this.state.selectedEvalRangeId;
        this._dialog.open(CommitmentDetailsDialogComponent, {
            movable: true,
            resizable: true,
            data: commitmentDialogData,
        });
    }
}
